<template>
  <section class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-ink">Enquiries</h2>
        <p class="text-sm text-muted">Review incoming venue and service vendor enquiries.</p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="loading || !enquiries.length"
          @click="exportEnquiriesCsv"
        >
          <i class="ri-file-text-line"></i>
          CSV
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="loading || !enquiries.length"
          @click="exportEnquiriesExcel"
        >
          <i class="ri-file-excel-2-line"></i>
          Excel
        </button>
        <button
          type="button"
          class="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
          :disabled="loading"
          @click="loadAllEnquiries"
        >
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div class="mt-5 inline-flex rounded-2xl border border-gray-200 bg-gray-50 p-1">
      <button
        v-for="tab in enquiryTabs"
        :key="tab.value"
        type="button"
        class="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition"
        :class="activeEnquiryType === tab.value ? 'bg-white text-brand shadow-sm' : 'text-muted hover:text-ink'"
        @click="setActiveEnquiryType(tab.value)"
      >
        <i :class="tab.icon"></i>
        {{ tab.label }}
        <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-muted">
          {{ tab.value === 'venue' ? venueEnquiries.length : vendorEnquiries.length }}
        </span>
      </button>
    </div>

    <div v-if="error" class="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
      {{ error }}
    </div>

    <div v-if="loading" class="mt-6 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-muted">
      Loading enquiries...
    </div>

    <div v-else class="mt-6 overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
      <div class="border-b border-gray-100 bg-white px-4 py-3">
        <p class="text-xs font-semibold uppercase tracking-wide text-muted">Enquiry feed</p>
      </div>
      <div class="space-y-3 p-3 text-sm sm:p-4">
        <article
          v-for="item in paginatedEnquiries"
          :key="item.id"
          class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
        >
          <div
            class="flex cursor-pointer flex-col gap-3 rounded-2xl px-1 py-1 transition hover:bg-gray-50 lg:flex-row lg:items-start lg:justify-between"
            role="button"
            tabindex="0"
            @click="toggleEnquiry(item.id)"
            @keydown.enter.prevent="toggleEnquiry(item.id)"
            @keydown.space.prevent="toggleEnquiry(item.id)"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="text-base font-semibold text-ink">{{ item.name }}</h3>
                <span class="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-muted">#{{ item.id }}</span>
              </div>
              <p class="mt-1 flex items-center gap-1.5 text-xs text-muted">
                <i class="ri-time-line"></i>
                Received {{ item.createdAt }}
              </p>
            </div>
            <div class="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              <button
                type="button"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-lg text-ink transition hover:border-brand hover:text-brand"
                :aria-label="isEnquiryCollapsed(item.id) ? 'Expand enquiry details' : 'Collapse enquiry details'"
                @click.stop="toggleEnquiry(item.id)"
              >
                <i :class="isEnquiryCollapsed(item.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'"></i>
              </button>
              <button
                type="button"
                class="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#eadfd4] bg-[#fffaf5] px-3 py-2 text-xs font-semibold text-[#6f5948] transition hover:border-brand hover:bg-white hover:text-brand sm:flex-none"
                @click.stop="openNotesModal(item)"
              >
                <i class="ri-sticky-note-line"></i>
                {{ hasNotes(item) ? 'Edit Note' : 'Add Note' }}
              </button>
            </div>
          </div>

          <div v-if="!isEnquiryCollapsed(item.id)" class="mt-4">
          <div class="grid gap-3 lg:grid-cols-2">
            <section class="overflow-hidden rounded-2xl border border-[#eadfd4] bg-[#fffdfa]">
              <div class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                <span class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#b58d72]">
                  <i class="ri-building-4-line"></i>
                  {{ activeEnquiryType === 'venue' ? 'Venue' : 'Vendor' }}
                </span>
              </div>
              <div class="border-t border-[#eadfd4] px-4 py-3">
                <div class="flex items-start gap-3">
                  <div class="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-[#eadfd4] bg-[#fffaf5]">
                    <img :src="item.venueImage" :alt="item.venueName" class="h-full w-full object-cover">
                  </div>
                  <div class="min-w-0">
                    <p class="truncate font-semibold text-ink">{{ item.venueName }}</p>
                    <p class="mt-1 flex items-start gap-1.5 text-xs leading-5 text-muted">
                      <i class="ri-map-pin-line mt-0.5 shrink-0 text-[#b58d72]"></i>
                      <span class="min-w-0">
                        {{ getVenueAddressText(item) }}
                        <button v-if="shouldShowAddressToggle(item)" type="button" class="ml-1 font-semibold text-brand transition hover:text-brandDark" @click.stop="toggleAddress(item.id)">
                          {{ isAddressExpanded(item.id) ? 'Less' : 'More' }}
                        </button>
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section class="overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
              <div class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
                <span class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
                  <i class="ri-contacts-line"></i>
                  Contact
                </span>
              </div>
              <div class="grid gap-2 border-t border-gray-100 px-4 py-3 text-xs">
                <div class="flex items-center gap-2 text-muted">
                  <i class="ri-phone-line text-[#6f5948]"></i>
                  <span class="min-w-0 truncate">{{ item.phone }}</span>
                </div>
                <div class="flex items-center gap-2 text-muted">
                  <i class="ri-mail-line text-[#6f5948]"></i>
                  <span class="min-w-0 truncate" :title="item.email">{{ item.email }}</span>
                </div>
              </div>
            </section>
          </div>

          <section v-if="activeEnquiryType === 'venue'" class="mt-3 overflow-hidden rounded-2xl border border-gray-100 bg-white">
            <div class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
              <span class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted">
                <i class="ri-calendar-event-line"></i>
                Event Details
              </span>
            </div>
            <div class="flex flex-wrap gap-2 border-t border-gray-100 px-4 py-3">
              <span class="inline-flex items-center gap-1.5 rounded-full bg-[#fff1e6] px-3 py-1.5 text-xs font-semibold text-[#8b5e3c]">
                <i class="ri-calendar-event-line"></i>
                {{ item.eventType }}
              </span>
              <span class="inline-flex items-center gap-1.5 rounded-full bg-[#eef6ff] px-3 py-1.5 text-xs font-semibold text-[#1d4f8f]">
                <i class="ri-time-line"></i>
                {{ item.session }}
              </span>
              <span class="inline-flex items-center gap-1.5 rounded-full bg-[#f2f8f3] px-3 py-1.5 text-xs font-semibold text-[#246b36]">
                <i class="ri-group-line"></i>
                {{ item.guestsCount }} guests
              </span>
            </div>
          </section>

          <section class="mt-3 overflow-hidden rounded-2xl bg-[#fffaf5] text-sm leading-6 text-[#6f5948]">
            <div class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
              <span class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#b58d72]">
                <i class="ri-file-list-3-line"></i>
                Requirements
              </span>
            </div>
            <div class="border-t border-[#eadfd4] px-4 py-3">
              <RichTextContent
                :style="getDetailsStyle(item)"
                :html="item.details"
                fallback="No details added."
              />
              <button v-if="shouldShowDetailsToggle(item)" type="button" class="mt-2 text-xs font-semibold text-brand transition hover:text-brandDark" @click="toggleDetails(item.id)">
                {{ isDetailsExpanded(item.id) ? 'Less' : 'More' }}
              </button>
            </div>
          </section>

          <section v-if="hasNotes(item)" class="mt-3 overflow-hidden rounded-2xl border border-blue-100 bg-blue-50 text-sm leading-6 text-blue-900">
            <div class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
              <span class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-500">
                <i class="ri-sticky-note-line"></i>
                Note
              </span>
            </div>
            <div class="border-t border-blue-100 px-4 py-3">
              <RichTextContent
                :style="getNotesStyle(item)"
                :html="item.notes"
                fallback=""
              />
              <button
                v-if="shouldShowNotesToggle(item)"
                type="button"
                class="mt-2 text-xs font-semibold text-blue-600 transition hover:text-blue-800"
                @click="toggleNotes(item.id)"
              >
                {{ isNotesExpanded(item.id) ? 'Less' : 'More' }}
              </button>
            </div>
          </section>
          </div>
        </article>
        <div v-if="!enquiries.length" class="px-4 py-6 text-center text-sm text-muted">
          No {{ activeEnquiryType === 'venue' ? 'venue' : 'vendor' }} enquiries found.
        </div>
      </div>
      <div v-if="enquiries.length" class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 text-sm">
        <p class="text-muted">
          Showing {{ paginationStart }}-{{ paginationEnd }} of {{ enquiries.length }}
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            <i class="ri-arrow-left-s-line"></i>
            Previous
          </button>
          <span class="rounded-full bg-[#fffaf5] px-3 py-1.5 text-xs font-semibold text-[#6f5948]">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            type="button"
            class="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-ink transition hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="notesModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 px-4 py-6"
      @click.self="closeNotesModal"
    >
      <div class="w-full max-w-lg overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        <div class="flex items-start justify-between gap-4 border-b border-gray-100 bg-gray-50 px-5 py-4">
          <div>
            <p class="text-xs font-bold uppercase tracking-wide text-brand">Enquiry note</p>
            <h3 class="mt-1 text-lg font-semibold text-ink">{{ selectedNoteEnquiry?.name || 'Guest' }}</h3>
            <p class="mt-1 text-sm text-muted">Enquiry #{{ selectedNoteEnquiry?.id }}</p>
          </div>
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-muted transition hover:border-brand hover:text-brand"
            @click="closeNotesModal"
          >
            <i class="ri-close-line text-lg"></i>
          </button>
        </div>
        <div class="px-5 py-4">
          <label class="mb-2 block text-sm font-semibold text-ink">Notes</label>
          <RichTextEditor
            v-model="notesDraft"
            class="enquiry-notes-editor"
            placeholder="Add notes for this enquiry"
          />
          <p v-if="notesError" class="mt-2 text-sm text-red-600">{{ notesError }}</p>
        </div>
        <div class="flex flex-wrap justify-end gap-3 border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            class="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink transition hover:border-brand hover:text-brand"
            :disabled="savingNotes"
            @click="closeNotesModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="savingNotes"
            @click="saveNotes"
          >
            {{ savingNotes ? 'Saving...' : 'Save Note' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import RichTextContent from '@/components/common/RichTextContent.vue'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import api from '@/services/vendor/api'
import { getAuthUserId } from '@/services/auth'

const activeEnquiryType = ref('venue')
const venueEnquiries = ref([])
const vendorEnquiries = ref([])
const loading = ref(false)
const error = ref('')
const expandedDetailsIds = ref(new Set())
const expandedNotesIds = ref(new Set())
const expandedAddressIds = ref(new Set())
const collapsedEnquiryIds = ref(new Set())
const DETAILS_PREVIEW_LIMIT = 140
const NOTES_PREVIEW_LIMIT = 140
const ADDRESS_PREVIEW_LIMIT = 50
const PAGE_SIZE = 10
const currentPage = ref(1)
const notesModalOpen = ref(false)
const selectedNoteEnquiry = ref(null)
const notesDraft = ref('')
const savingNotes = ref(false)
const notesError = ref('')
const loadedEnquiryTypes = ref(new Set())

const enquiryTabs = [
  { value: 'venue', label: 'Venues', icon: 'ri-building-4-line' },
  { value: 'vendor', label: 'Vendors', icon: 'ri-store-2-line' }
]
const enquiries = computed(() => activeEnquiryType.value === 'venue' ? venueEnquiries.value : vendorEnquiries.value)
const totalPages = computed(() => Math.max(1, Math.ceil(enquiries.value.length / PAGE_SIZE)))
const paginatedEnquiries = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE
  return enquiries.value.slice(start, start + PAGE_SIZE)
})
const paginationStart = computed(() => enquiries.value.length ? ((currentPage.value - 1) * PAGE_SIZE) + 1 : 0)
const paginationEnd = computed(() => Math.min(currentPage.value * PAGE_SIZE, enquiries.value.length))
const resetCollapsedEnquiries = () => {
  collapsedEnquiryIds.value = new Set(enquiries.value.map((item) => String(item.id)))
}
const resetListUiState = () => {
  currentPage.value = 1
  expandedDetailsIds.value = new Set()
  expandedNotesIds.value = new Set()
  expandedAddressIds.value = new Set()
  resetCollapsedEnquiries()
}
const setActiveEnquiryType = async (type) => {
  if (activeEnquiryType.value === type) return
  activeEnquiryType.value = type
  resetListUiState()
  if (!loadedEnquiryTypes.value.has(type)) {
    await loadEnquiries(type)
  }
}
const goToPage = (page) => {
  currentPage.value = Math.min(Math.max(1, page), totalPages.value)
  expandedDetailsIds.value = new Set()
  expandedNotesIds.value = new Set()
  expandedAddressIds.value = new Set()
  resetCollapsedEnquiries()
}

const isEnquiryCollapsed = (id) => collapsedEnquiryIds.value.has(String(id))
const toggleEnquiry = (id) => {
  const key = String(id)
  const next = new Set(collapsedEnquiryIds.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  collapsedEnquiryIds.value = next
}
const isDetailsExpanded = (id) => expandedDetailsIds.value.has(String(id))
const getPlainText = (value) => {
  const container = document.createElement('div')
  container.innerHTML = String(value || '')
  return (container.textContent || '').trim()
}
const hasNotes = (item) => Boolean(getPlainText(item?.notes))
const shouldShowDetailsToggle = (item) => getPlainText(item?.details).length > DETAILS_PREVIEW_LIMIT
const isNotesExpanded = (id) => expandedNotesIds.value.has(String(id))
const shouldShowNotesToggle = (item) => getPlainText(item?.notes).length > NOTES_PREVIEW_LIMIT
const isAddressExpanded = (id) => expandedAddressIds.value.has(String(id))
const shouldShowAddressToggle = (item) => String(item?.venueAddress || '').trim().length > ADDRESS_PREVIEW_LIMIT
const getVenueAddressText = (item) => {
  const address = String(item?.venueAddress || '').trim()
  if (!shouldShowAddressToggle(item) || isAddressExpanded(item.id)) return address
  return `${address.slice(0, ADDRESS_PREVIEW_LIMIT).trim()}...`
}
const toggleAddress = (id) => {
  const key = String(id)
  const next = new Set(expandedAddressIds.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  expandedAddressIds.value = next
}
const toggleDetails = (id) => {
  const key = String(id)
  const next = new Set(expandedDetailsIds.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  expandedDetailsIds.value = next
}
const toggleNotes = (id) => {
  const key = String(id)
  const next = new Set(expandedNotesIds.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  expandedNotesIds.value = next
}
const getDetailsStyle = (item) => {
  if (!shouldShowDetailsToggle(item) || isDetailsExpanded(item.id)) return {}
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    overflow: 'hidden'
  }
}
const getNotesStyle = (item) => {
  if (!shouldShowNotesToggle(item) || isNotesExpanded(item.id)) return {}
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    overflow: 'hidden'
  }
}

const exportColumns = [
  { key: 'id', label: 'Enquiry ID' },
  { key: 'name', label: 'Guest' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'venueName', label: 'Listing' },
  { key: 'venueAddress', label: 'Address' },
  { key: 'eventType', label: 'Event Type' },
  { key: 'session', label: 'Session' },
  { key: 'guestsCount', label: 'Guests' },
  { key: 'createdAt', label: 'Received' },
  { key: 'details', label: 'Requirements' },
  { key: 'notes', label: 'Notes' }
]

const exportFileName = (extension) => {
  const date = new Date().toISOString().slice(0, 10)
  return `${activeEnquiryType.value}-enquiries-${date}.${extension}`
}

const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const csvCell = (value) => {
  const text = getPlainText(value).replace(/\r?\n/g, '\n')
  return `"${text.replace(/"/g, '""')}"`
}

const exportEnquiriesCsv = () => {
  if (!enquiries.value.length) return
  const header = exportColumns.map((column) => csvCell(column.label)).join(',')
  const rows = enquiries.value.map((item) => (
    exportColumns.map((column) => csvCell(item[column.key])).join(',')
  ))
  downloadBlob(
    `\uFEFF${[header, ...rows].join('\r\n')}`,
    exportFileName('csv'),
    'text/csv;charset=utf-8;'
  )
}

const htmlCell = (value) => getPlainText(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/\r?\n/g, '<br>')

const exportEnquiriesExcel = () => {
  if (!enquiries.value.length) return
  const header = exportColumns
    .map((column) => `<th>${htmlCell(column.label)}</th>`)
    .join('')
  const rows = enquiries.value
    .map((item) => `<tr>${exportColumns.map((column) => `<td>${htmlCell(item[column.key])}</td>`).join('')}</tr>`)
    .join('')
  const html = `
    <html>
      <head>
        <meta charset="UTF-8">
        <style>
          table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 12px; }
          th { background: #fff1e6; font-weight: 700; }
          th, td { border: 1px solid #d9d9d9; padding: 8px; vertical-align: top; }
        </style>
      </head>
      <body>
        <table>
          <thead><tr>${header}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body>
    </html>
  `
  downloadBlob(
    html,
    exportFileName('xls'),
    'application/vnd.ms-excel;charset=utf-8;'
  )
}

const extractArrayPayload = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const displayValue = (value, fallback = 'N/A') => {
  const normalized = String(value ?? '').trim()
  if (!normalized || normalized === '0') return fallback
  return normalized
}

const formatDisplayName = (value, fallback = 'Guest') => {
  const normalized = displayValue(value, fallback)
  if (normalized === fallback) return normalized
  return normalized
    .toLowerCase()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase())
}

const buildVenuePlaceholderImage = (venueName) => {
  const letter = encodeURIComponent(String(venueName || 'V').trim().charAt(0).toUpperCase() || 'V')
  return `https://dummyimage.com/160x120/fff1e6/8b5e3c&text=${letter}`
}

const normalizeVenueImage = (value, venueName) => {
  const image = String(value ?? '').trim()
  return image && image !== '0' ? image : buildVenuePlaceholderImage(venueName)
}

const formatReceivedDate = (value) => {
  const raw = displayValue(value, '')
  if (!raw) return 'N/A'
  const date = new Date(raw.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return 'N/A'
  const day = String(date.getDate()).padStart(2, '0')
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear()
  let hour = date.getHours()
  const minute = String(date.getMinutes()).padStart(2, '0')
  const period = hour >= 12 ? 'PM' : 'AM'
  hour = hour % 12 || 12
  return `${day} ${month} ${year} ${String(hour).padStart(2, '0')}:${minute} ${period}`
}

const normalizeEnquiry = (item = {}, index = 0, type = activeEnquiryType.value) => {
  const phoneCode = String(item?.phone_code || item?.customer_phone_code || '').trim()
  const phone = String(item?.phone || item?.customer_phone || item?.mobile || '').trim()
  const listingFallback = type === 'venue' ? 'Venue not specified' : 'Vendor not specified'
  const venueName = displayValue(
    item?.venue_title ||
    item?.venue_name ||
    item?.venue ||
    item?.service_vendor_name ||
    item?.service_vendor ||
    item?.brand_name ||
    item?.business_name,
    listingFallback
  )
  return {
    id: String(item?.id || item?.enquiry_id || `enquiry-${index}`),
    name: formatDisplayName(item?.name || item?.customer_name || item?.full_name),
    email: String(item?.email || item?.customer_email || 'N/A').trim(),
    phone: phone ? `${phoneCode ? `${phoneCode} ` : ''}${phone}` : 'N/A',
    venueName,
    venueAddress: displayValue(item?.venue_address || item?.service_vendor_address || item?.address || item?.location),
    venueImage: normalizeVenueImage(item?.venue_image || item?.service_vendor_image || item?.cover_image || item?.image, venueName),
    eventType: displayValue(item?.event_type || item?.eventType),
    session: displayValue(item?.session || item?.event_session),
    guestsCount: displayValue(item?.guests_count || item?.guest_count || item?.guests),
    details: String(item?.details || item?.description || item?.message || 'No details added.').trim(),
    notes: String(item?.notes || item?.note || item?.vendor_notes || item?.enquiry_notes || '').trim(),
    createdAt: formatReceivedDate(item?.created_date || item?.created_at || item?.date)
  }
}

const openNotesModal = (item) => {
  selectedNoteEnquiry.value = item
  notesDraft.value = item?.notes || ''
  notesError.value = ''
  notesModalOpen.value = true
}

const closeNotesModal = () => {
  if (savingNotes.value) return
  notesModalOpen.value = false
  selectedNoteEnquiry.value = null
  notesDraft.value = ''
  notesError.value = ''
}

const saveNotes = async () => {
  const enquiry = selectedNoteEnquiry.value
  if (!enquiry?.id) {
    notesError.value = 'Enquiry id is missing.'
    return
  }
  savingNotes.value = true
  notesError.value = ''
  try {
    const payload = new FormData()
    payload.append('enquiry_id', enquiry.id)
    payload.append('notes', notesDraft.value || '')
    const response = await api.updateNotesEnquiry(payload)
    const ok = response?.data?.status === 'ok' || response?.data?.status === true || response?.data?.success === true
    if (!ok) {
      throw new Error(response?.data?.msg || response?.data?.message || 'Failed to update note.')
    }
    const next = enquiries.value.map((item) => (
      String(item.id) === String(enquiry.id)
        ? { ...item, notes: notesDraft.value || '' }
        : item
    ))
    if (activeEnquiryType.value === 'venue') {
      venueEnquiries.value = next
    } else {
      vendorEnquiries.value = next
    }
    savingNotes.value = false
    closeNotesModal()
  } catch (err) {
    notesError.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to update note.'
  } finally {
    savingNotes.value = false
  }
}

const getBusinessItems = (data) => extractArrayPayload(data, ['vendors', 'business_vendors', 'businessVendor', 'items', 'list'])
const getVenueItems = (data) => extractArrayPayload(data, ['venues', 'items', 'list', 'venue_list'])
const getServiceVendorItems = (data) => extractArrayPayload(data, ['service_vendors', 'vendors', 'items', 'list', 'service_vendor_list'])

const getEnquiryItems = (data) => extractArrayPayload(data, ['enquiries', 'venue_enquiries', 'service_vendor_enquiries', 'vendor_enquiries', 'items', 'list'])

const getEnquiryKey = (item = {}, index = 0) => String(
  item?.id ||
  item?.enquiry_id ||
  item?.vendor_enquiry_id ||
  item?.venue_enquiry_id ||
  `enquiry-${index}`
)

const uniqueEnquiryItems = (items = []) => {
  const seen = new Set()
  return items.filter((item, index) => {
    const key = getEnquiryKey(item, index)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const loadEnquiriesByVenue = async (vendorId) => {
  const businessPayload = new FormData()
  businessPayload.append('s_id', vendorId)
  const { data } = await api.listBusinessVendor(businessPayload)
  const businesses = getBusinessItems(data)
  const venueLists = await Promise.all(
    businesses.map(async (business) => {
      const businessId = String(business?.id || business?.business_id || business?.business_vendor_id || '').trim()
      if (!businessId) return []
      const venuePayload = new FormData()
      venuePayload.append('business_id', businessId)
      const response = await api.listVenuesBusinessVendor(venuePayload)
      return getVenueItems(response?.data)
    })
  )
  const venues = venueLists.flat()
  const enquiryLists = await Promise.all(
    venues.map(async (venue) => {
      const venueId = String(venue?.id || venue?.venue_id || venue?.vendor_business_venue_id || '').trim()
      if (!venueId) return []
      const payload = new FormData()
      payload.append('type', 'venue')
      payload.append('venue_id', venueId)
      const response = await api.getEnquiries(payload)
      return getEnquiryItems(response?.data)
    })
  )
  return uniqueEnquiryItems(enquiryLists.flat())
}

const loadEnquiriesByServiceVendor = async (vendorId) => {
  const businessPayload = new FormData()
  businessPayload.append('s_id', vendorId)
  const { data } = await api.listBusinessVendor(businessPayload)
  const businesses = getBusinessItems(data)
  const serviceVendorLists = await Promise.all(
    businesses.map(async (business) => {
      const businessId = String(business?.id || business?.business_id || business?.business_vendor_id || '').trim()
      if (!businessId) return []
      const serviceVendorPayload = new FormData()
      serviceVendorPayload.append('business_id', businessId)
      const response = await api.listServiceVendors(serviceVendorPayload)
      return getServiceVendorItems(response?.data)
    })
  )
  const serviceVendors = serviceVendorLists.flat()
  const enquiryLists = await Promise.all(
    serviceVendors.map(async (serviceVendor) => {
      const serviceVendorId = String(serviceVendor?.service_vendor_id || serviceVendor?.id || serviceVendor?.vendor_id || serviceVendor?.s_id || '').trim()
      if (!serviceVendorId) return []
      const payload = new FormData()
      payload.append('type', 'vendor')
      payload.append('service_vendor_id', serviceVendorId)
      const response = await api.getEnquiries(payload)
      return getEnquiryItems(response?.data)
    })
  )
  return uniqueEnquiryItems(enquiryLists.flat())
}

const assignEnquiries = (type, items) => {
  const normalized = uniqueEnquiryItems(items).map((item, index) => normalizeEnquiry(item, index, type))
  if (type === 'venue') {
    venueEnquiries.value = normalized
  } else {
    vendorEnquiries.value = normalized
  }
}

const fetchEnquiriesByType = (type, vendorId) => (
  type === 'venue'
    ? loadEnquiriesByVenue(vendorId)
    : loadEnquiriesByServiceVendor(vendorId)
)

const loadEnquiries = async (type = activeEnquiryType.value) => {
  loading.value = true
  error.value = ''
  if (type === 'venue') {
    venueEnquiries.value = []
  } else {
    vendorEnquiries.value = []
  }
  try {
    const vendorId = getAuthUserId()
    if (!vendorId) throw new Error('Vendor id is not available in auth storage.')

    const items = await fetchEnquiriesByType(type, vendorId)
    assignEnquiries(type, items)
    loadedEnquiryTypes.value = new Set([...loadedEnquiryTypes.value, type])
    if (type === activeEnquiryType.value) {
      resetListUiState()
    }
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to load enquiries.'
    if (type === 'venue') {
      venueEnquiries.value = []
    } else {
      vendorEnquiries.value = []
    }
  } finally {
    loading.value = false
  }
}

const loadAllEnquiries = async () => {
  loading.value = true
  error.value = ''
  venueEnquiries.value = []
  vendorEnquiries.value = []
  try {
    const vendorId = getAuthUserId()
    if (!vendorId) throw new Error('Vendor id is not available in auth storage.')

    const [venueItems, vendorItems] = await Promise.all([
      fetchEnquiriesByType('venue', vendorId),
      fetchEnquiriesByType('vendor', vendorId)
    ])
    assignEnquiries('venue', venueItems)
    assignEnquiries('vendor', vendorItems)
    loadedEnquiryTypes.value = new Set(['venue', 'vendor'])
    resetListUiState()
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Failed to load enquiries.'
    venueEnquiries.value = []
    vendorEnquiries.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadAllEnquiries()
})
</script>

<style scoped>
.enquiry-notes-editor {
  margin-top: 0;
  border-color: #e5e7eb;
  border-radius: 1rem;
}

.enquiry-notes-editor:focus-within {
  border-color: #c73d3d;
  box-shadow: 0 0 0 4px rgba(251, 146, 60, 0.18);
}

.enquiry-notes-editor :deep(.rich-text-editor__surface) {
  min-height: 140px;
  padding: 0.75rem 1rem;
  color: #1f2937;
  font-size: 0.875rem;
}
</style>
